/* amphr-product-gallery.js — PDP gallery ↔ lightbox glue.
 *
 * The PDP renders only the first few shots (see main-product.liquid) so the
 * page stays short; every remaining image lives in the lightbox, one per
 * screen. Opening and closing, focus trapping and Escape are handled by the
 * shared drawer system in amphr.js — this file only does what is specific to
 * the gallery: jumping to the shot that was clicked, paging with the arrows
 * and arrow keys, and keeping the counter honest.
 */
(function () {
  'use strict';

  function init() {
    var lb = document.querySelector('[data-pgal-lightbox]');
    if (!lb || lb.dataset.amphrBound) return;
    lb.dataset.amphrBound = '1';

    var track = lb.querySelector('[data-pgal-scroll]');
    var current = lb.querySelector('[data-pgal-current]');
    var slides = Array.prototype.slice.call(lb.querySelectorAll('[data-pgal-slide]'));
    var closeBtn = lb.querySelector('.pgal-lb__close');
    var arrows = Array.prototype.slice.call(lb.querySelectorAll('[data-pgal-step]'));
    if (!track || !slides.length) return;

    var index = 0;

    function goTo(next, behavior) {
      index = Math.max(0, Math.min(slides.length - 1, next));
      resetZoom();
      slides[index].scrollIntoView({ behavior: behavior || 'auto', block: 'nearest', inline: 'center' });
      render();
    }

    /* ── Click to zoom ──────────────────────────────────────────────────
       The pointer position becomes the transform origin, so the part that
       was clicked is the part that grows, and moving the mouse afterwards
       pans across the image. */
    function resetZoom() {
      slides.forEach(function (slide) {
        slide.classList.remove('is-zoomed');
        var img = slide.querySelector('img');
        if (img) img.style.transformOrigin = '';
      });
    }

    function setOrigin(img, e) {
      var box = img.getBoundingClientRect();
      var x = ((e.clientX - box.left) / box.width) * 100;
      var y = ((e.clientY - box.top) / box.height) * 100;
      img.style.transformOrigin = Math.max(0, Math.min(100, x)) + '% ' + Math.max(0, Math.min(100, y)) + '%';
    }

    slides.forEach(function (slide) {
      var img = slide.querySelector('img');
      if (!img) return;

      img.addEventListener('click', function (e) {
        var zoomed = slide.classList.contains('is-zoomed');
        resetZoom();
        if (zoomed) return;
        setOrigin(img, e);
        slide.classList.add('is-zoomed');
      });

      img.addEventListener('mousemove', function (e) {
        if (slide.classList.contains('is-zoomed')) setOrigin(img, e);
      });

      img.addEventListener('mouseleave', function () {
        slide.classList.remove('is-zoomed');
        img.style.transformOrigin = '';
      });
    });

    /* ── Video slides ────────────────────────────────────────────────────
       Only the slide on screen may make sound. play() is a promise that
       rejects when the browser refuses autoplay — swiping to a clip is not a
       user gesture — and an unhandled rejection would surface in the console
       even though the poster and the controls are still perfectly usable. */
    function syncVideos() {
      slides.forEach(function (slide, n) {
        var video = slide.querySelector('video');
        if (!video) return;
        if (n === index) {
          var played = video.play();
          if (played && played.catch) played.catch(function () {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    function stopAllMedia() {
      slides.forEach(function (slide) {
        var video = slide.querySelector('video');
        if (video && !video.paused) video.pause();
        /* Hosted iframes take no commands from here; reloading the frame is
           the only way to stop a YouTube or Vimeo clip still playing behind a
           closed overlay. */
        var frame = slide.querySelector('iframe');
        if (frame) frame.src = frame.src;
      });
    }

    function render() {
      if (current) current.textContent = String(index + 1);
      arrows.forEach(function (btn) {
        var step = parseInt(btn.getAttribute('data-pgal-step'), 10);
        btn.disabled = step < 0 ? index === 0 : index === slides.length - 1;
      });
      if (lb.classList.contains('on')) syncVideos();
    }

    new MutationObserver(function () {
      if (!lb.classList.contains('on')) stopAllMedia();
    }).observe(lb, { attributes: true, attributeFilter: ['class'] });

    /* Jump to the clicked shot. amphr.js opens the overlay on the same click,
       so we wait a frame for it to become visible — scrollIntoView is a no-op
       while the element is still visibility:hidden. */
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-pgal-index]');
      if (!trigger) return;
      var target = parseInt(trigger.getAttribute('data-pgal-index'), 10);
      if (isNaN(target) || !slides[target]) return;
      requestAnimationFrame(function () { goTo(target); });
      /* The overlay fades in over 300ms and focus() is refused until it has
         actually painted, so the drawer system's own focus call lands nowhere
         on the first open. Retry once the transition is done. */
      setTimeout(function () {
        if (closeBtn && lb.classList.contains('on') && !lb.contains(document.activeElement)) {
          closeBtn.focus({ preventScroll: true });
        }
      }, 340);
    });

    arrows.forEach(function (btn) {
      btn.addEventListener('click', function () {
        goTo(index + parseInt(btn.getAttribute('data-pgal-step'), 10), 'smooth');
      });
    });

    document.addEventListener('keydown', function (e) {
      if (!lb.classList.contains('on')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1, 'smooth'); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1, 'smooth'); }
    });

    /* Swipe and trackpad scrolling bypass goTo(), so the counter follows
       whichever slide actually covers the middle of the viewport. */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            index = slides.indexOf(entry.target);
            render();
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach(function (slide) { io.observe(slide); });
    }

    render();
  }

  /* ── Dots for the on-page rail (phones only) ──────────────────────────
     The rail is one full-bleed shot per swipe, so without a marker there is
     nothing on screen saying more images exist. Built from the DOM rather
     than rendered in Liquid: the count has to match whatever the rail
     actually holds, and a variant change can swap that. */
  function initRailDots() {
    var rail = document.querySelector('[data-pgal-rail]');
    var dots = document.querySelector('[data-pgal-dots]');
    if (!rail || !dots || dots.dataset.amphrBound) return;
    dots.dataset.amphrBound = '1';

    var slides = Array.prototype.slice.call(rail.querySelectorAll('.pim'));
    if (slides.length < 2) return;

    dots.hidden = false;
    slides.forEach(function () {
      var d = document.createElement('span');
      d.className = 'pgaldot';
      dots.appendChild(d);
    });
    var marks = Array.prototype.slice.call(dots.children);

    function paint(i) {
      marks.forEach(function (m, n) { m.classList.toggle('on', n === i); });
    }
    paint(0);

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) paint(slides.indexOf(entry.target));
          });
        },
        { root: rail, threshold: 0.6 }
      );
      slides.forEach(function (s) { io.observe(s); });
    }
  }

  function initAll() {
    init();
    initRailDots();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Theme editor re-renders the section without a page load. */
  document.addEventListener('shopify:section:load', initAll);
})();
